/**
 * Per-user voice tuning defaults. These are short-lived UI preferences (slider
 * positions and named presets), so localStorage is the right home — no
 * server-side state depends on them.
 */
import type { Voice } from "./api";

export interface VoiceSettings {
  stability: number;
  similarity_boost: number;
  style: number;
  speed: number;
  use_speaker_boost: boolean;
}

export interface Preset {
  name: string;
  voiceKey: string;
  settings: VoiceSettings;
  savedAt: number;
}

const NS = "podgo.voiceStudio";

/**
 * Every key is namespaced by the signed-in account. A shared browser would
 * otherwise hand the next person whatever draft the previous session left
 * behind (localStorage has no notion of who wrote it).
 */
const presetsKey = (userId: string) => `${NS}.${userId}.presets`;
const currentKey = (userId: string) => `${NS}.${userId}.current`;
const draftKey = (userId: string) => `${NS}.${userId}.draft`;

/** Pre-namespacing keys: unattributable, so they are dropped rather than migrated. */
const LEGACY_KEYS = [`${NS}.presets`, `${NS}.current`, `${NS}.draft`];

export function purgeLegacyVoicePrefs() {
  try {
    LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* private mode — nothing to purge */
  }
}

/** Wipes every Voice Studio local key (all accounts) — used on sign-out. */
export function clearAllVoicePrefs() {
  try {
    purgeLegacyVoicePrefs();
    Object.keys(localStorage)
      .filter((k) => k.startsWith(`${NS}.`))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* private mode — nothing to clear */
  }
}


export const DEFAULT_SETTINGS: VoiceSettings = {
  stability: 0.5,
  similarity_boost: 0.75,
  style: 0.0,
  speed: 1.0,
  use_speaker_boost: true,
};

export const SLIDER_META: {
  key: Exclude<keyof VoiceSettings, "use_speaker_boost">;
  label: string;
  min: number;
  max: number;
  step: number;
  hint: string;
}[] = [
  { key: "stability", label: "Stability", min: 0, max: 1, step: 0.01, hint: "Lower is more expressive; higher is more consistent" },
  { key: "similarity_boost", label: "Similarity", min: 0, max: 1, step: 0.01, hint: "How closely to match the source voice" },
  { key: "style", label: "Style", min: 0, max: 1, step: 0.01, hint: "Emphasize the source voice's speaking style" },
  { key: "speed", label: "Speed", min: 0.7, max: 1.2, step: 0.01, hint: "Playback rate multiplier" },
];

/** The voice's own profile defaults, falling back to the global defaults. */
export function fromVoice(v: Voice | undefined): VoiceSettings {
  const s = (v?.settings || {}) as Record<string, unknown>;
  const num = (k: keyof VoiceSettings, fallback: number) =>
    typeof s[k] === "number" ? (s[k] as number) : fallback;
  return {
    stability: num("stability", DEFAULT_SETTINGS.stability),
    similarity_boost: num("similarity_boost", DEFAULT_SETTINGS.similarity_boost),
    style: num("style", DEFAULT_SETTINGS.style),
    speed: num("speed", DEFAULT_SETTINGS.speed),
    use_speaker_boost: !!s.use_speaker_boost,
  };
}

export function loadPresets(userId: string | null | undefined): Preset[] {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(presetsKey(userId));
    return raw ? (JSON.parse(raw) as Preset[]) : [];
  } catch {
    return [];
  }
}

export function savePresets(userId: string | null | undefined, presets: Preset[]) {
  if (!userId) return;
  localStorage.setItem(presetsKey(userId), JSON.stringify(presets));
}

export function saveCurrent(
  userId: string | null | undefined,
  voiceKey: string,
  settings: VoiceSettings
) {
  if (!userId) return;
  localStorage.setItem(currentKey(userId), JSON.stringify({ voiceKey, settings }));
}

export function loadCurrent(
  userId: string | null | undefined,
  voiceKey: string
): VoiceSettings | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(currentKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { voiceKey?: string; settings?: VoiceSettings };
    return parsed.voiceKey === voiceKey ? parsed.settings ?? null : null;
  } catch {
    return null;
  }
}

/**
 * Keystroke-level copy of the Studio composer. Written synchronously on every
 * change so a crash or accidental navigation loses nothing; the database copy
 * (voiceDraftService) is the debounced, cross-device one.
 */
export interface LocalScriptDraft {
  title: string;
  /** Show this episode is destined for; chosen in the Episode section. */
  showId?: string;
  description: string;
  script: string;
  voiceKey: string;
  settings: VoiceSettings | null;
  updatedAt: number;
}

export function saveScriptDraft(
  userId: string | null | undefined,
  draft: Omit<LocalScriptDraft, "updatedAt">
) {
  if (!userId) return;
  try {
    localStorage.setItem(
      draftKey(userId),
      JSON.stringify({ ...draft, updatedAt: Date.now() } satisfies LocalScriptDraft)
    );
  } catch {
    /* quota or private mode — the remote copy still covers the user */
  }
}

export function loadScriptDraft(
  userId: string | null | undefined
): LocalScriptDraft | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(draftKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocalScriptDraft;
    return typeof parsed?.updatedAt === "number" ? parsed : null;
  } catch {
    return null;
  }
}

export function clearScriptDraft(userId: string | null | undefined) {
  if (!userId) return;
  localStorage.removeItem(draftKey(userId));
}
